import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { DownloadIcon, FileTextIcon } from "lucide-react";
import type { ExtractedEntity } from "@/lib/http/pii-file-scan";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { documentRedactorState } from "@/lib/document-redactor-state";

export const Route = createFileRoute("/_app/document-redactor/result")({
  component: RouteComponent,
  beforeLoad() {
    if (!documentRedactorState.getResult())
      throw redirect({ to: "/document-redactor" });
  },
});

function RouteComponent() {
  const redactResult = documentRedactorState.getResult();

  const navigate = useNavigate();
  const handleClickStart = () => {
    documentRedactorState.clear();
    navigate({ to: "/document-redactor" });
  };
  if (!redactResult) return null;

  return (
    <div>
      <div className="mb-8 flex justify-between space-y-2 max-md:flex-col">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Document Redactor
          </h1>
          <p className="text-muted-foreground">
            Masks/Highlights PIIs on your files
          </p>
        </div>
        <div>
          <Button
            variant="outline"
            className="mt-5 flex h-10 cursor-pointer items-center gap-2 max-md:w-full"
            onClick={handleClickStart}
          >
            <FileTextIcon className="size-4" />
            <span>Start New</span>
          </Button>
        </div>
      </div>

      {redactResult.scan_type === "annotate" ? (
        <>
          <Card className="flex justify-center justify-items-center">
            <span className="m-auto max-md:text-center">
              Your document has been redacted successfully
            </span>
          </Card>
          <div className="mt-8 flex justify-center">
            <a
              className={cn(
                buttonVariants({ variant: "outline" }),
                "border-primary text-primary",
              )}
              href={redactResult.output_file_url}
              target="_blank"
              rel="noreferrer"
              download
            >
              <DownloadIcon />
              Download
            </a>
          </div>
        </>
      ) : (
        <Card className="gap-4 p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">Detected entities</h2>
              <p className="text-sm text-muted-foreground">
                {redactResult.file_name}
              </p>
            </div>
            <div className="flex gap-2">
              <Badge variant="secondary">
                {redactResult.entities.length} entities
              </Badge>
              {redactResult.num_tokens != null && (
                <Badge variant="outline">
                  {redactResult.num_tokens} tokens
                </Badge>
              )}
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Text</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redactResult.entities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-6 text-center">
                      No entities detected
                    </TableCell>
                  </TableRow>
                ) : (
                  redactResult.entities.map((entity: ExtractedEntity, index: number) => (
                    <TableRow
                      key={`${entity.entity_type}-${entity.start}-${entity.end}-${index}`}
                    >
                      <TableCell>{entity.entity_type}</TableCell>
                      <TableCell>{entity.entity_text ?? "—"}</TableCell>
                      <TableCell>{entity.start ?? "—"}</TableCell>
                      <TableCell>{entity.end ?? "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
